import { Box, Group, useMantineTheme } from "@mantine/core"

const SIGNAL_SIZE = {
  xs: { barWidth: 4, gap: 3, baseHeight: 6, heightStep: 5, borderRadius: 2 },
  sm: { barWidth: 5, gap: 4, baseHeight: 8, heightStep: 6, borderRadius: 2 },
  md: { barWidth: 6, gap: 5, baseHeight: 10, heightStep: 8, borderRadius: 3 },
  lg: { barWidth: 8, gap: 6, baseHeight: 12, heightStep: 10, borderRadius: 4 },
} as const

export type SignalIndicatorSize = keyof typeof SIGNAL_SIZE

export type SignalIndicatorProps = {
  /** Filled bar count: `0` = none, up to `maxLevel` when fully active. */
  readonly level: number
  readonly maxLevel: number
  /** Accessible description (e.g. translated compliance level). */
  readonly ariaLabel: string
  /** Visual scale of bars and spacing. Default matches previous fixed sizing (`md`). */
  readonly size?: SignalIndicatorSize
}

const clampLevel = (level: number, maxLevel: number): number =>
  Math.min(maxLevel, Math.max(0, level))

/**
 * Phone-style signal bars: ascending bar heights, first `level` bars active.
 */
export const SignalIndicator = ({
  level,
  maxLevel,
  ariaLabel,
  size = "md",
}: SignalIndicatorProps) => {
  const { colors } = useMantineTheme()
  const activeLevel = clampLevel(level, maxLevel)
  const inactiveColor = colors.gray[4]
  const activeColor = colors.primary[4]
  const { barWidth, gap, baseHeight, heightStep, borderRadius } =
    SIGNAL_SIZE[size]

  return (
    <Group gap={gap} align="flex-end" role="img" aria-label={ariaLabel}>
      {Array.from({ length: maxLevel }, (_, index) => {
        const barOrder = index + 1
        const isActive = barOrder <= activeLevel
        const heightPx = baseHeight + index * heightStep
        return (
          <Box
            key={barOrder}
            w={barWidth}
            h={heightPx}
            style={{
              borderRadius,
              backgroundColor: isActive ? activeColor : inactiveColor,
            }}
          />
        )
      })}
    </Group>
  )
}
